import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Plus } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AreaOfFraud } from "../../../api/cases";
import { CreateStakeholderRequest } from "../../../api/stakeholders";
import { EntityType, RelatedAs, StakeholderRelationshipFields } from "../../../api/stakeholder_relationships";
import { isEmployeeRole } from "../../../api/accounts";
import { createStakeholders } from "../../stakeholder/logic/stakeholderThunks";
import { getStakeholders } from "../../stakeholder/logic/stakeholderSlice";
import { selectAccounts, selectAreaOfFraud } from "../../caseinfo/logic/Slice";
import ManagementModal from "../../../../src/components/ui/modals/ManagementModal";
import ButtonV2 from "../../../components/ui/button/v2";
import AccountPartyRow, { AddAccountPartyRow, isAccountPartyValid } from "../../create-case/components/party-involved/AccountPartyRow";
import EmployeeModal, { EmployeePartyItem } from "./EmployeeModal";

interface AccountPartyModalProps {
  onClose: () => void;
  onBack?: () => void;
}

const buildEmptyParty = (): AddAccountPartyRow => {
  const stakeholderId = uuidv4();
  return {
    stakeholder: {
      stakeholder_id: stakeholderId,
      name: "",
      is_potential_perpetrator: true,
    },
    relationshipGroups: [{id: uuidv4(), relatedAs: undefined, accountNumbers: []}],
  };
};

const AccountPartyModal: React.FC<AccountPartyModalProps> = ({ onClose, onBack }) => {
  const dispatch = useAppDispatch();
  const { id: caseId } = useParams<{ id: string }>();

  const areaOfFraud = useAppSelector(selectAreaOfFraud) ?? AreaOfFraud.OPERATIONAL;
  const accounts = useAppSelector(selectAccounts);
  const existingStakeholders = useAppSelector(getStakeholders);

  const [showErrors, setShowErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [parties, setParties] = useState<AddAccountPartyRow[]>([buildEmptyParty()]);

  const employeeParties: EmployeePartyItem[] = useMemo(() => {
    return parties
      .filter((party) =>
        (party.relationshipGroups || []).some((r) => r.relatedAs && isEmployeeRole(r.relatedAs, areaOfFraud))
      )
      .map((party) => ({
        partyId: party.stakeholder.stakeholder_id,
        name: party.stakeholder.name,
        types: (party.relationshipGroups || [])
          .map((r) => r.relatedAs as RelatedAs)
          .filter((role) => role && isEmployeeRole(role, areaOfFraud)),
      }));
  }, [parties, areaOfFraud]);

  const hasEmployeeTypes = employeeParties.length > 0;
  const areAllPartiesValid =
    parties.length > 0 &&
    parties.every((party) => isAccountPartyValid(party, parties, existingStakeholders));

  const handleAddParty = () => {
    if (!areAllPartiesValid) {
      setShowErrors(true);
      return;
    }
    setParties((prev) => [...prev, buildEmptyParty()]);
  };

  const handleDeleteParty = (stakeholderId: string) => {
    setParties((prev) => prev.filter((p) => p.stakeholder.stakeholder_id !== stakeholderId));
  };

  const handleChangeParty = (updated: AddAccountPartyRow) => {
    setParties((prev) =>
      prev.map((p) => (p.stakeholder.stakeholder_id === updated.stakeholder.stakeholder_id ? updated : p))
    );
  };

  const handleSubmit = () => {
    setShowErrors(true);
    if (!areAllPartiesValid) return;
    if (hasEmployeeTypes) {
      setEmployeeModalOpen(true);
      return;
    }
    handleCreateStakeholders();
  };

  const handleCreateStakeholders = async (empMap: Record<string, string> = {}) => {
    if (!caseId || !areAllPartiesValid) return;
    setIsSaving(true);

    const stakeholders: CreateStakeholderRequest[] = parties.map((p) => {
    const relationships: StakeholderRelationshipFields[] = p.relationshipGroups.flatMap((group) => (
        group.accountNumbers.map((an) => ({
            entity_type: EntityType.ACCOUNT,
            entity_id: an,
            related_as: group.relatedAs ?? RelatedAs.OTHER,
        }))
    ))

      const empId = empMap[p.stakeholder.stakeholder_id];

      return {
        stakeholder: {
          name: p.stakeholder.name.trim(),
          is_potential_perpetrator: true,
        },
        relationships: relationships.length > 0 ? relationships : undefined,
        ...(empId && { employee_details: { employee_id: empId } }),
      };
    });

    try {
      await dispatch(createStakeholders({ caseId, request: { stakeholders } })).unwrap();
      setEmployeeModalOpen(false);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (employeeModalOpen) {
    return (
      <EmployeeModal
        employees={employeeParties}
        onClose={onClose}
        onSave={handleCreateStakeholders}
        onBack={() => setEmployeeModalOpen(false)}
      />
    );
  }

  return (
    <ManagementModal
      title="Add Party"
      onClose={onClose}
      onCancel={onClose}
      onBack={onBack}
      onSubmit={handleSubmit}
      submitLabel={hasEmployeeTypes ? "Provide Employee Details" : "Save"}
      cancelLabel="Discard"
      submitEnabled={!isSaving}
      isLoading={isSaving}
      className="w-[932px] max-w-[80vw]"
    >
      <div className="flex flex-col gap-6">
        {parties.map((party) => (
          <AccountPartyRow
            key={party.stakeholder.stakeholder_id}
            party={party}
            allAdded={parties}
            allExisting={existingStakeholders}
            accounts={accounts}
            area_of_fraud={areaOfFraud}
            showErrors={showErrors}
            onChange={handleChangeParty}
            onDelete={parties.length > 1 ? () => handleDeleteParty(party.stakeholder.stakeholder_id)
                : () => {}
            }
          />
        ))}

        <ButtonV2
          variant="ghost"
          size="sm"
          disabled={!areAllPartiesValid}
          onClick={handleAddParty}
          className="text-[#3551F3] bg-transparent self-start disabled:opacity-40 hover:bg-transparent hover:text-[#304ADD] px-0 h-auto"
          prefix={<Plus width="16" height="16" />}
        >
          Add Primary Party
        </ButtonV2>
      </div>
    </ManagementModal>
  );
};

export default AccountPartyModal;